import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { BoardNote } from '@/bryntum-lib/models/BoardNote';
import { EntityRecord } from '@/types/entity';

interface NotesEditorContextValue {
  noteRecord: BoardNote;
  entityRecord: EntityRecord;
  isNewNote: boolean;
  isSaveDisabled: boolean;
  isSettingsOpen: boolean;
  wordCount: { words: number; characters: number };
  onSettingsToggle: () => void;
  onDeleteNote?: () => void;
  onClose: () => void;
}

const NotesEditorContext = createContext<NotesEditorContextValue | undefined>(undefined);

interface NotesEditorProviderProps extends NotesEditorContextValue {
  children: ReactNode;
}

export const NotesEditorProvider: React.FC<NotesEditorProviderProps> = ({ children, ...value }) => {
  const contextValue = useMemo(
    () => ({
      noteRecord: value.noteRecord,
      entityRecord: value.entityRecord,
      isNewNote: value.isNewNote,
      isSaveDisabled: value.isSaveDisabled,
      isSettingsOpen: value.isSettingsOpen,
      wordCount: value.wordCount,
      onSettingsToggle: value.onSettingsToggle,
      onDeleteNote: value.onDeleteNote,
      onClose: value.onClose
    }),
    [
      value.noteRecord,
      value.entityRecord,
      value.isNewNote,
      value.isSaveDisabled,
      value.isSettingsOpen,
      value.wordCount,
      value.onSettingsToggle,
      value.onDeleteNote,
      value.onClose
    ]
  );

  return <NotesEditorContext.Provider value={contextValue}>{children}</NotesEditorContext.Provider>;
};

export const useNotesEditorContext = (): NotesEditorContextValue => {
  const context = useContext(NotesEditorContext);
  if (!context) {
    throw new Error('useNotesEditorContext must be used within NotesEditorProvider');
  }
  return context;
};
